import React, { useState } from "react";
import { Play, FileText, Download } from "lucide-react";
import { mediaUrl } from "../../lib/blogApi";

const no = (lang) => lang !== "en";

function ClickToLoad({ b, lang, testid }) {
  const [on, setOn] = useState(false);
  const label = b.title || b.url;
  if (on) {
    return (
      <div className="aspect-video w-full border border-ink bg-ink">
        <iframe data-testid={`${testid}-iframe`} title={label} src={b.embed_src} className="w-full h-full"
          allow="encrypted-media; fullscreen; picture-in-picture" allowFullScreen loading="lazy" />
      </div>
    );
  }
  return (
    <button data-testid={testid} type="button" onClick={() => setOn(true)}
      className="group w-full aspect-video border border-ink bg-sand flex flex-col items-center justify-center gap-3 text-ink hover:bg-ink hover:text-paper focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-ink motion-safe:transition-colors">
      <Play size={36} aria-hidden="true" />
      <span className="font-serif text-xl px-6 text-center">{label}</span>
      <span className="font-mono text-[12px] tracking-[0.14em] uppercase">
        {no(lang) ? "Last inn og spill av" : "Load and play"} · {b.type === "embed" ? "slides" : b.type}
      </span>
    </button>
  );
}

function Figure({ children, caption }) {
  return (
    <figure className="my-10">
      {children}
      {caption && <figcaption className="mt-3 text-sm text-[#4a4a4a]">{caption}</figcaption>}
    </figure>
  );
}

function PdfSlides({ b, lang }) {
  const url = mediaUrl(b.media_id);
  return (
    <Figure caption={b.caption}>
      <div className="border border-ink">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-ink bg-sand">
          <span className="flex items-center gap-2 font-serif text-lg"><FileText size={18} aria-hidden="true" />{b.title || "Slides"}</span>
          <a data-testid="blog-pdf-download" href={url} download
            className="inline-flex items-center gap-2 font-mono text-[12px] tracking-[0.12em] uppercase underline underline-offset-4 hover:text-[#7a2d00]">
            <Download size={14} aria-hidden="true" />{no(lang) ? "Last ned PDF" : "Download PDF"}{b.pages ? ` (${b.pages} s.)` : ""}
          </a>
        </div>
        <object data={`${url}#view=FitH`} type="application/pdf" className="w-full h-[70vh] min-h-[420px]" aria-label={b.title || "Slides"}>
          <p className="p-4">{no(lang) ? "PDF kan ikke vises her." : "PDF can't be shown here."} <a href={url} className="underline">{no(lang) ? "Åpne PDF" : "Open PDF"}</a></p>
        </object>
      </div>
      {b.pdf_text && (
        <details className="mt-3 border border-rule p-4">
          <summary className="cursor-pointer font-mono text-[12px] tracking-[0.12em] uppercase">{no(lang) ? "Tekstversjon av slidene" : "Text version of the slides"}</summary>
          <pre className="mt-3 whitespace-pre-wrap font-sans text-[15px] leading-relaxed">{b.pdf_text}</pre>
        </details>
      )}
    </Figure>
  );
}

export const BlogBlocks = ({ blocks, lang }) => (
  <>
    {blocks.map((b, i) => {
      const k = `${b.type}-${i}`;
      if (b.type === "text") return <div key={k} className="blog-prose" dangerouslySetInnerHTML={{ __html: b.html }} />;
      if (b.type === "image" && b.media_id)
        return (
          <Figure key={k} caption={b.caption}>
            <img src={mediaUrl(b.media_id)} alt={b.alt || ""} loading="lazy" className="w-full h-auto border border-ink" />
          </Figure>
        );
      if (["youtube", "vimeo", "embed"].includes(b.type) && b.embed_src)
        return <Figure key={k} caption={b.caption}><ClickToLoad b={b} lang={lang} testid={`blog-embed-${i}`} /></Figure>;
      if (b.type === "video" && b.media_id) {
        const tr = b.transcript || b.transcript_auto;
        return (
          <Figure key={k} caption={b.caption}>
            <video controls preload="metadata" className="w-full border border-ink bg-ink" aria-label={b.title || "Video"}>
              <source src={mediaUrl(b.media_id)} />
              {b.captions_media_id && <track kind="captions" src={mediaUrl(b.captions_media_id)} srcLang={lang === "en" ? "en" : lang === "da" ? "da" : "nb"} label="Undertekster" default />}
            </video>
            {tr && (
              <details className="mt-3 border border-rule p-4">
                <summary className="cursor-pointer font-mono text-[12px] tracking-[0.12em] uppercase">{no(lang) ? "Transkripsjon" : "Transcript"}</summary>
                <p className="mt-3 leading-relaxed">{tr}</p>
              </details>
            )}
          </Figure>
        );
      }
      if (b.type === "pdf" && b.media_id) return <PdfSlides key={k} b={b} lang={lang} />;
      return null;
    })}
  </>
);
